/* ============================================================
   Backyournation — theme JS
   ============================================================ */
(function () {
  'use strict';

  // ---------- Global toast utility (window.BYNToast) ----------
  // Micro-confirmations for actions that don't open a drawer/modal. Renders
  // a stacked pill at bottom-right with auto-dismiss + fade. Multiple toasts
  // stack vertically. Exposed on window so any module can call it.
  const Toast = (function initToast() {
    const container = document.createElement('div');
    container.className = 'byn-toast-stack';
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'true');
    document.addEventListener('DOMContentLoaded', () => {
      document.body.appendChild(container);
    }, { once: true });
    // If already loaded (defer), append immediately
    if (document.readyState !== 'loading') {
      document.body.appendChild(container);
    }

    function show(message, opts) {
      const { type = 'success', duration = 2200 } = opts || {};
      const toast = document.createElement('div');
      toast.className = 'byn-toast byn-toast--' + type;
      const icon = type === 'success'
        ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>'
        : type === 'error'
        ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
        : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
      toast.innerHTML = '<span class="byn-toast__icon" aria-hidden="true">' + icon + '</span>'
        + '<span class="byn-toast__msg">' + String(message).replace(/</g, '&lt;') + '</span>';
      container.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('is-visible'));
      setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => toast.remove(), 300);
      }, duration);
    }
    return { show };
  })();
  window.BYNToast = Toast;

  // ---------- Mobile menu toggle ----------
  const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
  const nav = document.querySelector('.header__nav');
  const closeMenu = () => {
    nav?.classList.remove('is-open');
    document.body.classList.remove('nav-open');
    menuToggle?.setAttribute('aria-expanded', 'false');
  };
  if (menuToggle && nav) {
    menuToggle.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      document.body.classList.toggle('nav-open', open);
      menuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Tap-toggle submenu on mobile (parent link expands submenu on first tap,
    // navigates on second tap when it's already open). Runs BEFORE the
    // close-on-link handler below so the shared click event can be gated
    // via `data-submenu-expand-only` when we intercepted the navigation.
    nav.querySelectorAll('.has-submenu > .header__menu-link').forEach(link => {
      link.addEventListener('click', e => {
        if (!window.matchMedia('(max-width: 833px)').matches) return;
        const parent = link.closest('.has-submenu');
        if (!parent.classList.contains('is-open')) {
          e.preventDefault();
          // Mark this click as an "expand only" — the close-on-link handler
          // below will see the flag and skip closing the whole drawer.
          e.__bynExpandOnly = true;
          nav.querySelectorAll('.has-submenu.is-open').forEach(s => { if (s !== parent) s.classList.remove('is-open'); });
          parent.classList.add('is-open');
        }
      });
    });

    // Close whole nav drawer when a LEAF link is tapped (not when expanding a
    // parent submenu). The flag set above short-circuits the close path so
    // first-tap on "HOME ⌄" expands its submenu instead of closing everything.
    nav.querySelectorAll('a').forEach(a => a.addEventListener('click', (e) => {
      if (!window.matchMedia('(max-width: 833px)').matches) return;
      if (e.__bynExpandOnly) return;
      closeMenu();
    }));

    // Close on Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) closeMenu();
    });

    // Close on resize past breakpoint
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!window.matchMedia('(max-width: 833px)').matches) closeMenu();
      }, 100);
    });
  }

  // ---------- All Teams modal ----------
  const teamsModal = document.getElementById('AllTeamsModal');
  if (teamsModal) {
    const openBtn = document.querySelector('[data-all-teams-open]');
    const closeEls = teamsModal.querySelectorAll('[data-all-teams-close]');
    const search = teamsModal.querySelector('[data-all-teams-search]');
    const items = teamsModal.querySelectorAll('.all-teams-item');
    const emptyMsg = teamsModal.querySelector('[data-all-teams-empty]');
    let lastFocus = null;

    const openModal = () => {
      lastFocus = document.activeElement;
      teamsModal.hidden = false;
      document.body.classList.add('nav-open');
      requestAnimationFrame(() => search?.focus());
    };
    const closeModal = () => {
      teamsModal.hidden = true;
      document.body.classList.remove('nav-open');
      if (search) { search.value = ''; filterTeams(''); }
      lastFocus?.focus();
    };
    const filterTeams = q => {
      const term = q.trim().toLowerCase();
      let visible = 0;
      items.forEach(it => {
        const name = it.dataset.teamName || '';
        const match = !term || name.includes(term);
        it.classList.toggle('is-hidden', !match);
        if (match) visible++;
      });
      if (emptyMsg) emptyMsg.hidden = visible > 0;
    };

    openBtn?.addEventListener('click', openModal);
    closeEls.forEach(el => el.addEventListener('click', closeModal));
    search?.addEventListener('input', e => filterTeams(e.target.value));
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !teamsModal.hidden) closeModal();
    });
    // Close modal when clicking a team link (so navigation feels natural)
    items.forEach(it => it.addEventListener('click', () => setTimeout(closeModal, 50)));
  }

  // ---------- Price range slider (dual-handle) ----------
  document.querySelectorAll('[data-price-slider]').forEach(slider => {
    const minThumb  = slider.querySelector('[data-price-thumb-min]');
    const maxThumb  = slider.querySelector('[data-price-thumb-max]');
    const minLabel  = slider.querySelector('[data-price-label-min]');
    const maxLabel  = slider.querySelector('[data-price-label-max]');
    const active    = slider.querySelector('[data-price-track-active]');
    const minHidden = slider.querySelector('[data-price-input-min]');
    const maxHidden = slider.querySelector('[data-price-input-max]');
    if (!minThumb || !maxThumb) return;

    const absMin = Number(slider.dataset.absMin) || 0;
    const absMax = Number(slider.dataset.absMax) || 100;
    const range  = absMax - absMin;
    const minGap = 5; // minimum gap between handles (in dollars, matches step)

    const fmt = (v) => '$' + v;

    const paint = () => {
      const minV = Number(minThumb.value);
      const maxV = Number(maxThumb.value);
      const lPct = ((minV - absMin) / range) * 100;
      const rPct = ((maxV - absMin) / range) * 100;
      active.style.left  = lPct + '%';
      active.style.width = (rPct - lPct) + '%';
      if (minLabel) minLabel.textContent = fmt(minV);
      if (maxLabel) maxLabel.textContent = fmt(maxV);
      if (minHidden) minHidden.value = minV;
      if (maxHidden) maxHidden.value = maxV;
    };

    minThumb.addEventListener('input', () => {
      // Push max away if min crosses it
      if (Number(minThumb.value) > Number(maxThumb.value) - minGap) {
        minThumb.value = Number(maxThumb.value) - minGap;
      }
      paint();
    });
    maxThumb.addEventListener('input', () => {
      if (Number(maxThumb.value) < Number(minThumb.value) + minGap) {
        maxThumb.value = Number(minThumb.value) + minGap;
      }
      paint();
    });

    paint();
  });

  // ---------- Collection filter drawer (mobile) ----------
  (function initFilterDrawer() {
    const toggle = document.querySelector('[data-filter-toggle]');
    const closer = document.querySelector('[data-filter-close]');
    if (!toggle) return;

    const open = () => {
      document.body.classList.add('filters-open');
      toggle.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      document.body.classList.remove('filters-open');
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', open);
    closer?.addEventListener('click', close);

    // Click on backdrop (the ::before pseudo) — detect via body click outside panel
    document.addEventListener('click', e => {
      if (!document.body.classList.contains('filters-open')) return;
      const panel = document.querySelector('[data-filter-panel]');
      if (!panel) return;
      if (panel.contains(e.target) || toggle.contains(e.target)) return;
      close();
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && document.body.classList.contains('filters-open')) close();
    });
  })();

  // ---------- Header search: predictive + recent + inline (desktop) ----------
  // Wires two search surfaces against the same /search/suggest.json backend:
  //   • Mobile drawer (data-search-drawer)  — icon-triggered slide-down
  //   • Desktop inline bar (data-header-inline-search) — always visible
  // Playbook: visible search box beats hidden icon (LYST +43% desktop / +13% mobile).
  (function initSearch() {
    const RECENT_KEY = 'byn.recentSearches.v1';
    const RECENT_MAX = 6;

    const escapeHtml = (str) => String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    // ------- Recent searches storage -------
    const readRecent = () => {
      try {
        const raw = localStorage.getItem(RECENT_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.filter(s => typeof s === 'string' && s.length) : [];
      } catch { return []; }
    };
    const writeRecent = (arr) => {
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, RECENT_MAX))); }
      catch { /* storage full or blocked — silently skip */ }
    };
    const pushRecent = (term) => {
      const clean = String(term || '').trim();
      if (clean.length < 2) return;
      const cur = readRecent().filter(s => s.toLowerCase() !== clean.toLowerCase());
      cur.unshift(clean);
      writeRecent(cur);
      renderRecent();
    };
    const clearRecent = () => { writeRecent([]); renderRecent(); };

    const renderRecent = () => {
      const wrap = document.querySelector('[data-search-recent]');
      const list = document.querySelector('[data-search-recent-chips]');
      if (!wrap || !list) return;
      const items = readRecent();
      if (!items.length) { wrap.hidden = true; list.innerHTML = ''; return; }
      list.innerHTML = items.map(term =>
        '<a class="header-search__chip" href="/search?q=' + encodeURIComponent(term) + '">' + escapeHtml(term) + '</a>'
      ).join('');
      wrap.hidden = false;
    };
    renderRecent();
    document.querySelector('[data-search-recent-clear]')?.addEventListener('click', clearRecent);

    // Save on any submit of a search form in the header
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action$="/search"]');
      if (!form) return;
      const inp = form.querySelector('input[name="q"]');
      if (inp) pushRecent(inp.value);
    });

    // ------- Predictive suggestions (attached per-input) -------
    async function fetchSuggestions(term) {
      const url = '/search/suggest.json?q=' + encodeURIComponent(term)
        + '&resources[type]=product'
        + '&resources[limit]=8'
        + '&resources[options][unavailable_products]=last';
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('Search fetch failed');
      const data = await res.json();
      return data.resources?.results?.products || [];
    }

    function renderResults(resultsEl, term, products) {
      if (!products.length) {
        resultsEl.innerHTML = '<p class="header-search__empty">No products match "' + escapeHtml(term) + '". Try different keywords.</p>';
        resultsEl.hidden = false;
        return;
      }
      const items = products.map(p => {
        const img = p.image ? '<img src="' + p.image + '" alt="" loading="lazy">' : '<img alt="" loading="lazy">';
        // /search/suggest.json returns price already formatted ("$29.99")
        const price = p.price || '';
        return '<a class="header-search__result" href="' + p.url + '">'
          + img
          + '<div class="header-search__result-body">'
          + '<p class="header-search__result-title">' + escapeHtml(p.title) + '</p>'
          + (price ? '<p class="header-search__result-price">' + escapeHtml(price) + '</p>' : '')
          + '</div>'
          + '</a>';
      }).join('');
      const moreUrl = '/search?q=' + encodeURIComponent(term);
      resultsEl.innerHTML =
        '<div class="header-search__group">' +
          '<h4 class="header-search__group-title">Products</h4>' +
          items +
        '</div>' +
        '<a class="header-search__more" href="' + moreUrl + '">See all results →</a>';
      resultsEl.hidden = false;
    }

    function attachPredictive(input, resultsEl, opts) {
      if (!input || !resultsEl) return;
      const minLen = 2;
      let timer;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const term = input.value.trim();
        if (term.length < minLen) {
          resultsEl.innerHTML = '';
          resultsEl.hidden = true;
          opts?.onEmpty?.();
          return;
        }
        opts?.onNonEmpty?.();
        timer = setTimeout(async () => {
          try { renderResults(resultsEl, term, await fetchSuggestions(term)); }
          catch (err) {
            console.error('[search]', err);
            resultsEl.innerHTML = '<p class="header-search__empty">Search unavailable, try again.</p>';
            resultsEl.hidden = false;
          }
        }, 220);
      });
    }

    // ------- Mobile drawer -------
    (function initDrawer() {
      // Multiple triggers can open the drawer: the header icon AND the bottom-nav
      // search item both use [data-search-toggle]. querySelectorAll gathers all
      // of them so every trigger fires the same open handler.
      const toggles = document.querySelectorAll('[data-search-toggle]');
      const drawer  = document.querySelector('[data-search-drawer]');
      const input   = document.querySelector('[data-search-input]');
      const closeBtn = document.querySelector('[data-search-close]');
      const results = document.querySelector('[data-search-results]');
      const emptyState = document.querySelector('[data-search-empty-state]');
      if (!toggles.length || !drawer || !input) return;

      const openDrawer = () => {
        // If the mobile nav is open (hamburger drawer), close it first so the
        // search drawer isn't hidden behind it AND the two overlays don't fight.
        const mobileNav = document.querySelector('.header__nav');
        if (mobileNav?.classList.contains('is-open')) {
          mobileNav.classList.remove('is-open');
          document.body.classList.remove('nav-open');
          document.querySelector('[data-mobile-menu-toggle]')?.setAttribute('aria-expanded', 'false');
        }
        drawer.hidden = false;
        toggles.forEach(t => t.setAttribute('aria-expanded', 'true'));
        if (emptyState) emptyState.hidden = false;
        // Use preventScroll so focusing the input on iOS Safari doesn't yank
        // the page back to the top of the document (the drawer is now fixed
        // to the viewport so scrolling to it is unnecessary).
        requestAnimationFrame(() => input.focus({ preventScroll: true }));
      };
      const closeDrawer = () => {
        drawer.hidden = true;
        toggles.forEach(t => t.setAttribute('aria-expanded', 'false'));
        input.value = '';
        if (results) { results.innerHTML = ''; results.hidden = true; }
        if (emptyState) emptyState.hidden = false;
      };
      toggles.forEach(t => t.addEventListener('click', (e) => {
        e.preventDefault();
        drawer.hidden ? openDrawer() : closeDrawer();
      }));
      closeBtn?.addEventListener('click', closeDrawer);
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !drawer.hidden) closeDrawer();
      });
      // Click outside the drawer AND outside every toggle closes it
      document.addEventListener('click', e => {
        if (drawer.hidden) return;
        if (drawer.contains(e.target)) return;
        for (const t of toggles) { if (t.contains(e.target)) return; }
        closeDrawer();
      });

      attachPredictive(input, results, {
        onNonEmpty: () => { if (emptyState) emptyState.hidden = true; },
        onEmpty:    () => { if (emptyState) emptyState.hidden = false; }
      });
    })();

    // ------- Desktop inline bar -------
    (function initInline() {
      const form    = document.querySelector('[data-header-inline-search]');
      const input   = document.querySelector('[data-header-inline-input]');
      const results = document.querySelector('[data-header-inline-results]');
      if (!form || !input || !results) return;

      attachPredictive(input, results);

      // Click outside the inline form closes the results dropdown
      document.addEventListener('click', e => {
        if (results.hidden) return;
        if (form.contains(e.target)) return;
        results.hidden = true;
      });
      // Refocus reopens if there's already a term
      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2 && results.innerHTML) results.hidden = false;
      });
    })();
  })();

  // ---------- Carousel (testimonials, etc.) ----------
  document.querySelectorAll('[data-carousel]').forEach(initCarousel);

  function initCarousel(carousel) {
    const track = carousel.querySelector('[data-carousel-track]');
    const prev  = carousel.querySelector('[data-carousel-prev]');
    const next  = carousel.querySelector('[data-carousel-next]');
    const dotsContainer = carousel.parentElement.querySelector('[data-carousel-dots]');
    if (!track) return;
    const slides = Array.from(track.querySelectorAll('[data-carousel-slide]'));
    if (!slides.length) return;

    const slideWidth = () => slides[0].getBoundingClientRect().width;
    const gap = () => parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 20;
    const step = () => slideWidth() + gap();

    const updateNav = () => {
      if (!prev || !next) return;
      const max = track.scrollWidth - track.clientWidth - 4;
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= max;
    };

    prev?.addEventListener('click', () => {
      track.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next?.addEventListener('click', () => {
      track.scrollBy({ left: step(), behavior: 'smooth' });
    });

    // Build dots — one per slide, only if more than 1 slide
    let dots = [];
    if (dotsContainer && slides.length > 1) {
      slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'testimonials__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', () => {
          slides[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
        dotsContainer.appendChild(dot);
      });
      dots = Array.from(dotsContainer.querySelectorAll('.testimonials__dot'));
    }

    const syncActive = () => {
      const sl = track.scrollLeft;
      let idx = 0;
      slides.forEach((s, i) => {
        if (s.offsetLeft - track.offsetLeft <= sl + slideWidth() / 2) idx = i;
      });
      dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      updateNav();
    };

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { syncActive(); ticking = false; });
    });
    window.addEventListener('resize', syncActive);
    updateNav();
  }

  // ---------- Testimonial photo lightbox (click to zoom) ----------
  (function initTestimonialLightbox() {
    const lightbox = document.querySelector('[data-testimonial-lightbox]');
    if (!lightbox) return;

    const imageEl   = lightbox.querySelector('[data-testimonial-lightbox-image]');
    const captionEl = lightbox.querySelector('[data-testimonial-lightbox-caption]');
    let lastFocus = null;

    const open = (src, caption) => {
      if (!src) return;
      lastFocus = document.activeElement;
      imageEl.src = src;
      imageEl.alt = caption || '';
      captionEl.textContent = caption || '';
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lightbox-open');
      // Force reflow so the transition runs
      void lightbox.offsetWidth;
      lightbox.classList.add('is-open');
      const closeBtn = lightbox.querySelector('[data-testimonial-lightbox-close]');
      closeBtn && closeBtn.focus();
    };

    const close = () => {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lightbox-open');
      setTimeout(() => {
        lightbox.hidden = true;
        imageEl.removeAttribute('src');
        captionEl.textContent = '';
        if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      }, 200);
    };

    // Delegate clicks on any review photo
    document.addEventListener('click', e => {
      const photo = e.target.closest('[data-testimonial-photo]');
      if (!photo) return;
      e.preventDefault();
      open(photo.dataset.zoomSrc || photo.getAttribute('src'),
           photo.dataset.zoomCaption || photo.getAttribute('alt'));
    });

    // Backdrop / close button
    lightbox.querySelectorAll('[data-testimonial-lightbox-close]').forEach(el =>
      el.addEventListener('click', close));

    // ESC to close
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && lightbox.classList.contains('is-open')) close();
    });
  })();

  // ---------- Product gallery (thumb-click swaps main) ----------
  document.querySelectorAll('[data-gallery]').forEach(gallery => {
    const main   = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelector('[data-gallery-thumbs]');
    if (!main || !thumbs) return;

    thumbs.addEventListener('click', e => {
      const btn = e.target.closest('[data-media-id]');
      if (!btn) return;
      const id = btn.dataset.mediaId;
      thumbs.querySelectorAll('[data-media-id]').forEach(t =>
        t.classList.toggle('is-active', t.dataset.mediaId === id));
      main.querySelectorAll('[data-media-id]').forEach(s =>
        s.classList.toggle('is-active', s.dataset.mediaId === id));
    });
  });

  // ---------- Quantity input ----------
  document.querySelectorAll('.quantity-input').forEach(wrap => {
    const input = wrap.querySelector('input');
    const minus = wrap.querySelector('[data-qty-minus]');
    const plus = wrap.querySelector('[data-qty-plus]');
    if (!input) return;
    minus?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value) - 1); input.dispatchEvent(new Event('change', { bubbles: true })); });
    plus?.addEventListener('click', () => { input.value = Number(input.value) + 1; input.dispatchEvent(new Event('change', { bubbles: true })); });
  });

  // ---------- Smart variant picker ----------
  // Wires every .product-form on the page against its sibling [data-product-meta]
  // JSON (emitted outside the form). Resolves the Style/Size/Color/Brand/Model
  // combo, updates price / SKU / CTA / URL, and greys out unavailable values.
  document.querySelectorAll('.product-form').forEach(initVariantPicker);

  function initVariantPicker(form) {
    // Find the meta script — first look inside the same product section,
    // fall back to the document for resilience against markup tweaks.
    const section = form.closest('.product-detail') || document;
    const metaEl  = section.querySelector('[data-product-meta]') || document.querySelector('[data-product-meta]');
    if (!metaEl) return;

    let meta;
    try { meta = JSON.parse(metaEl.textContent); } catch (e) {
      console.error('[variant picker] invalid meta JSON', e);
      return;
    }
    const variants = meta.variants || [];
    if (!variants.length) return;

    const moneyFormat = metaEl.dataset.moneyFormat || '${{amount}}';
    const variantIdInput = form.querySelector('[data-variant-id]');
    const submitBtn      = form.querySelector('[data-product-submit]');
    const submitText     = form.querySelector('[data-product-submit-text]');
    const submitPrice    = form.querySelector('[data-product-submit-price]');
    const priceWrap      = section.querySelector('[data-product-price]');
    const skuEl          = section.querySelector('[data-product-sku]');
    const availEl        = section.querySelector('[data-product-availability]');
    const optionGroups   = form.querySelectorAll('[data-option-position]');

    const formatMoney = (cents) => {
      if (cents == null) return '';
      const dollars = (cents / 100).toFixed(2);
      const whole = Math.round(cents / 100).toString();
      return moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/g, dollars)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole);
    };

    const getSelected = () => {
      const sel = [];
      optionGroups.forEach(group => {
        const checked = group.querySelector('input[type="radio"]:checked');
        const select  = group.querySelector('select');
        if (checked) sel.push(checked.value);
        else if (select) sel.push(select.value);
        else sel.push(null);
      });
      return sel;
    };

    const matchVariant = (combo) => variants.find(v =>
      (v.option1 == null || combo[0] == null || v.option1 === combo[0]) &&
      (v.option2 == null || combo[1] == null || v.option2 === combo[1]) &&
      (v.option3 == null || combo[2] == null || v.option3 === combo[2])
    );

    const findExactVariant = (selected) => variants.find(v =>
      (v.option1 == null ? !selected[0] : v.option1 === selected[0]) &&
      (v.option2 == null ? !selected[1] : v.option2 === selected[1]) &&
      (v.option3 == null ? !selected[2] : v.option3 === selected[2])
    );

    const markUnavailable = (selected) => {
      optionGroups.forEach(group => {
        const pos = Number(group.dataset.optionPosition) - 1;
        group.querySelectorAll('input[type="radio"]').forEach(input => {
          const test = [...selected];
          test[pos] = input.value;
          const match = matchVariant(test);
          const ok = match && match.available;
          input.closest('.pd-pill')?.classList.toggle('is-unavailable', !ok);
        });
        group.querySelectorAll('select option').forEach(opt => {
          if (!opt.value) return;
          const test = [...selected];
          test[pos] = opt.value;
          const match = matchVariant(test);
          const ok = match && match.available;
          opt.disabled = !ok;
          const baseLabel = opt.dataset.label || opt.value;
          opt.textContent = ok ? baseLabel : `${baseLabel} — Unavailable`;
        });
      });
    };

    const update = () => {
      const selected = getSelected();
      const variant = findExactVariant(selected);

      if (variantIdInput) variantIdInput.value = variant ? variant.id : '';

      optionGroups.forEach((group, i) => {
        const echo = group.querySelector('[data-selected-value]');
        if (echo) echo.textContent = selected[i] || '';
        group.querySelectorAll('.pd-pill').forEach(label => {
          const input = label.querySelector('input[type="radio"]');
          label.classList.toggle('is-selected', !!(input && input.checked));
        });
      });

      if (priceWrap && variant) {
        const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (sale) {
          const saved = variant.compare_at_price - variant.price;
          priceWrap.innerHTML =
            '<span class="pd-price__now">' + formatMoney(variant.price) + '</span>' +
            '<s class="pd-price__was">' + formatMoney(variant.compare_at_price) + '</s>' +
            '<span class="pd-price__save">Save ' + formatMoney(saved) + '</span>';
        } else {
          priceWrap.innerHTML = '<span class="pd-price__now">' + formatMoney(variant.price) + '</span>';
        }
      }

      if (submitBtn && submitText) {
        if (!variant) {
          submitBtn.disabled = true;
          submitText.textContent = 'Unavailable';
          if (submitPrice) submitPrice.textContent = '';
        } else if (!variant.available) {
          submitBtn.disabled = true;
          submitText.textContent = 'Sold out';
          if (submitPrice) submitPrice.textContent = '';
        } else {
          submitBtn.disabled = false;
          submitText.textContent = 'Add to cart';
          if (submitPrice) submitPrice.textContent = formatMoney(variant.price);
        }
      }

      if (skuEl) skuEl.textContent = variant && variant.sku ? variant.sku : '';
      if (availEl) {
        if (!variant) availEl.textContent = 'Unavailable';
        else if (variant.available) availEl.textContent = 'In stock';
        else availEl.textContent = 'Sold out';
      }

      // Per-variant featured image — swap main image + active thumb
      if (variant && variant.featured_media_id) {
        const targetId = String(variant.featured_media_id);
        const gallery  = section.querySelector('[data-gallery]');
        if (gallery) {
          gallery.querySelectorAll('.pd-gallery__slide').forEach(s =>
            s.classList.toggle('is-active', s.dataset.mediaId === targetId));
          gallery.querySelectorAll('.pd-gallery__thumb').forEach(t =>
            t.classList.toggle('is-active', t.dataset.mediaId === targetId));
        }
      }

      if (variant && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      markUnavailable(selected);
    };

    form.addEventListener('change', e => {
      if (e.target.matches('input[type="radio"], select')) update();
    });

    // Long-list filter — typing narrows the visible pills (Model: ~80 values)
    form.querySelectorAll('[data-picker-filter]').forEach(input => {
      input.addEventListener('input', e => {
        const term = e.target.value.trim().toLowerCase();
        const pills = input.closest('.pd-picker').querySelectorAll('.pd-pill');
        pills.forEach(p => {
          const v = (p.dataset.value || '').toLowerCase();
          p.style.display = !term || v.includes(term) ? '' : 'none';
        });
      });
    });

    // Hardened click handler — covers browsers where label→input forwarding
    // doesn't fire change (some iOS Safari versions) OR future CSS changes
    // that break the implicit label association. We don't bail when
    // input.checked is already true, because the browser may have set it
    // while skipping the change event — so we run update() unconditionally.
    form.addEventListener('click', e => {
      const pill = e.target.closest('.pd-pill');
      if (!pill || !form.contains(pill)) return;
      const input = pill.querySelector('input[type="radio"]');
      if (!input) return;
      const group = pill.parentElement;
      group.querySelectorAll('input[type="radio"][name="' + input.name + '"]').forEach(i => i.checked = false);
      input.checked = true;
      group.querySelectorAll('.pd-pill').forEach(p => p.classList.remove('is-selected'));
      pill.classList.add('is-selected');
      update();
    });

    // Initial paint — flag combos already sold-out on load
    markUnavailable(getSelected());
  }

  // ---------- Cart drawer ----------
  const CartDrawer = (function () {
    const drawer    = document.querySelector('[data-cart-drawer]');
    if (!drawer) return null;

    const itemsEl   = drawer.querySelector('[data-cart-items]');
    const emptyEl   = drawer.querySelector('[data-cart-empty]');
    const footerEl  = drawer.querySelector('[data-cart-footer]');
    const countEl   = drawer.querySelector('[data-cart-drawer-count]');
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    const checkoutAmt = drawer.querySelector('[data-cart-checkout-amount]');
    const closers   = drawer.querySelectorAll('[data-cart-drawer-close]');

    const formatMoney = (cents) => '$' + (cents / 100).toFixed(2);
    const escapeHtml = (str) => String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');

    let cartData = null;
    let fetchPromise = null;

    const open = async () => {
      drawer.hidden = false;
      document.body.classList.add('cart-open');
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      drawer.setAttribute('aria-hidden', 'false');
      // Fetch fresh cart if we haven't yet, OR if it's stale
      await refresh();
    };

    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-open');
      // Wait for transition before hiding (so animation can play)
      setTimeout(() => { drawer.hidden = true; }, 300);
    };

    const refresh = async () => {
      if (fetchPromise) return fetchPromise;
      fetchPromise = fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(r => r.json())
        .then(data => { cartData = data; render(); updateHeaderCount(); fetchPromise = null; })
        .catch(err => { console.error('[cart]', err); fetchPromise = null; });
      return fetchPromise;
    };

    let lastCount = -1;
    const updateHeaderCount = () => {
      if (!cartData) return;
      const n = cartData.item_count;
      const changed = lastCount !== -1 && lastCount !== n;
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = n > 0 ? n : '';
        if (changed && n > 0) {
          el.classList.remove('is-updated');
          // Force reflow so the animation restarts when re-added
          void el.offsetWidth;
          el.classList.add('is-updated');
          setTimeout(() => el.classList.remove('is-updated'), 500);
        }
      });
      if (countEl) countEl.textContent = n;
      lastCount = n;
    };

    const shippingBar   = drawer.querySelector('[data-cart-shipping-bar]');
    const shippingMsg   = drawer.querySelector('[data-cart-shipping-msg]');
    const shippingFill  = drawer.querySelector('[data-cart-shipping-fill]');

    const updateShippingBar = () => {
      if (!shippingBar || !cartData) return;
      const threshold = parseInt(shippingBar.dataset.thresholdCents || '0', 10);
      if (!threshold || threshold <= 0) { shippingBar.hidden = true; return; }
      const subtotal = cartData.items_subtotal_price ?? cartData.total_price ?? 0;
      const remaining = threshold - subtotal;
      const pct = Math.max(0, Math.min(100, (subtotal / threshold) * 100));
      if (shippingFill) shippingFill.style.width = pct.toFixed(1) + '%';
      if (shippingMsg) {
        if (remaining <= 0) {
          shippingMsg.innerHTML = '<b>You unlocked FREE shipping</b> 🎉';
          shippingBar.classList.add('is-unlocked');
        } else {
          shippingMsg.innerHTML = 'Add <b>' + formatMoney(remaining) + '</b> more for FREE shipping';
          shippingBar.classList.remove('is-unlocked');
        }
      }
      shippingBar.hidden = false;
    };

    const render = () => {
      if (!cartData) return;
      const items = cartData.items || [];
      if (items.length === 0) {
        itemsEl.innerHTML = '';
        emptyEl.hidden = false;
        footerEl.hidden = true;
        return;
      }
      emptyEl.hidden = true;
      footerEl.hidden = false;

      itemsEl.innerHTML = items.map(renderItem).join('');
      subtotalEl.textContent = formatMoney(cartData.items_subtotal_price ?? cartData.total_price);
      if (checkoutAmt) checkoutAmt.textContent = '· ' + formatMoney(cartData.total_price);
      updateShippingBar();
      loadUpsell(items[0]);
    };

    // ---- Upsell strip: fetch product recommendations for the first cart line ----
    const upsellWrap  = drawer.querySelector('[data-cart-upsell]');
    const upsellStrip = drawer.querySelector('[data-cart-upsell-strip]');
    let lastUpsellFor = null;

    async function loadUpsell(firstItem) {
      if (!upsellWrap || !upsellStrip || !firstItem) return;
      const productId = firstItem.product_id;
      if (!productId) { upsellWrap.hidden = true; return; }
      // Skip if we've already loaded recs for this product this drawer open
      if (lastUpsellFor === productId && upsellStrip.children.length > 0) return;
      lastUpsellFor = productId;
      try {
        const url = '/recommendations/products.json?product_id=' + encodeURIComponent(productId)
          + '&limit=6&intent=related';
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('upsell fetch failed');
        const data = await res.json();
        const cartHandles = new Set((cartData?.items || []).map(i => i.handle));
        const recs = (data.products || []).filter(p => !cartHandles.has(p.handle)).slice(0, 4);
        if (!recs.length) { upsellWrap.hidden = true; return; }
        upsellStrip.innerHTML = recs.map(p => {
          const img = (p.featured_image || '').replace(/(\.(?:jpe?g|png|webp|gif|avif))(\?.*)?$/i, '_240x$1$2');
          const price = formatMoney(p.price);
          const compare = p.compare_at_price && p.compare_at_price > p.price
            ? '<s>' + formatMoney(p.compare_at_price) + '</s>' : '';
          return ''
            + '<div class="cart-drawer__upsell-item">'
              + '<a href="' + p.url + '" class="cart-drawer__upsell-image">'
                + (img ? '<img src="' + img + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '')
              + '</a>'
              + '<div class="cart-drawer__upsell-body">'
                + '<a href="' + p.url + '" class="cart-drawer__upsell-name">' + escapeHtml(p.title) + '</a>'
                + '<p class="cart-drawer__upsell-price">' + price + ' ' + compare + '</p>'
                + '<button type="button" class="cart-drawer__upsell-add" data-upsell-variant="' + p.variants[0].id + '">+ Add</button>'
              + '</div>'
            + '</div>';
        }).join('');
        upsellWrap.hidden = false;
      } catch (err) {
        console.warn('[cart upsell]', err);
        upsellWrap.hidden = true;
      }
    }

    // Add-to-cart click on any upsell item
    upsellStrip?.addEventListener('click', async e => {
      const btn = e.target.closest('[data-upsell-variant]');
      if (!btn) return;
      const variantId = btn.dataset.upsellVariant;
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        const fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', '1');
        await addItem(fd);
        btn.textContent = 'Added ✓';
        setTimeout(() => { btn.disabled = false; btn.textContent = original; }, 1500);
      } catch (err) {
        console.error('[cart upsell add]', err);
        btn.disabled = false;
        btn.textContent = original;
      }
    });

    const renderItem = (item) => {
      // /cart.js returns both .image (string) and .featured_image (object) — prefer the object
      const rawImg = (item.featured_image && item.featured_image.url) || item.image || '';
      // Ask Shopify CDN for a small-ish render; safe-fall to original if pattern doesn't match
      const img = rawImg.replace(/(\.(?:jpe?g|png|webp|gif|avif))(\?.*)?$/i, '_180x$1$2');
      const variantParts = [];
      (item.options_with_values || item.variant_options || []).forEach(opt => {
        if (opt && opt.value && opt.value !== 'Default Title') {
          variantParts.push(escapeHtml(opt.name ? opt.name + ': ' + opt.value : opt.value));
        }
      });
      // Fallback: split variant_title
      if (variantParts.length === 0 && item.variant_title && item.variant_title !== 'Default Title') {
        variantParts.push(escapeHtml(item.variant_title));
      }
      const variantLine = variantParts.length ? '<p class="cart-drawer__item-variant">' + variantParts.join(' · ') + '</p>' : '';

      // Line-item properties (e.g. Personalize Name / Number). Skip Shopify's
      // internal keys (those prefixed with `_`).
      const propParts = [];
      if (item.properties) {
        Object.keys(item.properties).forEach(k => {
          if (k.charAt(0) === '_') return;
          const v = item.properties[k];
          if (v === null || v === undefined || v === '') return;
          propParts.push('<span class="cart-drawer__item-prop"><b>' + escapeHtml(k) + ':</b> ' + escapeHtml(String(v)) + '</span>');
        });
      }
      const propsLine = propParts.length ? '<p class="cart-drawer__item-props">' + propParts.join('') + '</p>' : '';

      const hasCompare = item.original_price && item.original_price > item.final_price;
      const priceHtml = hasCompare
        ? '<del>' + formatMoney(item.original_price) + '</del>' + formatMoney(item.final_price)
        : formatMoney(item.final_price);

      return ''
        + '<div class="cart-drawer__item" data-key="' + item.key + '">'
          + '<a href="' + item.url + '" class="cart-drawer__item-image">'
            + (img ? '<img src="' + img + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy">' : '')
          + '</a>'
          + '<div class="cart-drawer__item-body">'
            + '<p class="cart-drawer__item-title"><a href="' + item.url + '">' + escapeHtml(item.product_title) + '</a></p>'
            + variantLine
            + propsLine
            + '<p class="cart-drawer__item-price">' + priceHtml + '</p>'
            + '<div class="cart-drawer__item-controls">'
              + '<div class="cart-drawer__qty" data-qty>'
                + '<button type="button" data-qty-dec aria-label="Decrease quantity">−</button>'
                + '<span class="cart-drawer__qty-value">' + item.quantity + '</span>'
                + '<button type="button" data-qty-inc aria-label="Increase quantity">+</button>'
              + '</div>'
              + '<button type="button" class="cart-drawer__remove" data-remove>Remove</button>'
            + '</div>'
          + '</div>'
        + '</div>';
    };

    const changeQuantity = async (key, qty) => {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        if (!res.ok) throw new Error('Cart change failed');
        cartData = await res.json();
        render();
        updateHeaderCount();
      } catch (err) {
        console.error('[cart]', err);
        alert('Could not update cart. Please try again.');
      }
    };

    // Event delegation on the items container
    itemsEl?.addEventListener('click', e => {
      const itemEl = e.target.closest('[data-key]');
      if (!itemEl) return;
      const key = itemEl.dataset.key;

      if (e.target.closest('[data-qty-inc]')) {
        const cur = Number(itemEl.querySelector('.cart-drawer__qty-value').textContent) || 0;
        changeQuantity(key, cur + 1);
      } else if (e.target.closest('[data-qty-dec]')) {
        const cur = Number(itemEl.querySelector('.cart-drawer__qty-value').textContent) || 0;
        if (cur <= 1) changeQuantity(key, 0);
        else changeQuantity(key, cur - 1);
      } else if (e.target.closest('[data-remove]')) {
        changeQuantity(key, 0);
      }
    });

    closers.forEach(el => el.addEventListener('click', close));
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !drawer.hidden) close();
    });

    return { open, close, refresh, addItem };

    async function addItem(formData) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ description: 'Add to cart failed' }));
        throw new Error(err.description || 'Add to cart failed');
      }
      await refresh();
      open();
    }
  })();

  // Intercept header cart icon clicks → open drawer
  document.addEventListener('click', e => {
    const trigger = e.target.closest('[data-cart-drawer-open]');
    if (!trigger || !CartDrawer) return;
    e.preventDefault();
    CartDrawer.open();
  });

  // Initial fetch so header count + drawer are warm on page load
  CartDrawer?.refresh();

  // ---------- Cart page (server-rendered lines, AJAX quantity/remove) ----------
  (function initCartPage() {
    const list = document.querySelector('[data-cart-page-items]');
    if (!list) return;

    async function change(key, qty) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        if (!res.ok) throw new Error('Cart change failed');
        location.reload();
      } catch (err) {
        console.error('[cart page]', err);
        alert('Could not update cart. Please try again.');
      }
    }

    list.addEventListener('click', e => {
      const line = e.target.closest('[data-key]');
      if (!line) return;
      const key = line.dataset.key;
      const valEl = line.querySelector('[data-qty-value]');
      const cur = Number(valEl?.textContent) || 0;

      if (e.target.closest('[data-cart-line-inc]')) {
        change(key, cur + 1);
      } else if (e.target.closest('[data-cart-line-dec]')) {
        change(key, Math.max(0, cur - 1));
      } else if (e.target.closest('[data-cart-line-remove]')) {
        change(key, 0);
      }
    });

    // Order note auto-save on blur
    const note = document.querySelector('[data-cart-note]');
    if (note) {
      let lastVal = note.value;
      note.addEventListener('blur', async () => {
        if (note.value === lastVal) return;
        lastVal = note.value;
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ note: note.value })
          });
        } catch (err) { console.error('[cart note]', err); }
      });
    }
  })();

  // ---------- Size guide modal ----------
  (function initSizeGuide() {
    const modal = document.querySelector('[data-size-guide-modal]');
    if (!modal) return;
    const open = () => {
      modal.hidden = false;
      requestAnimationFrame(() => modal.classList.add('is-open'));
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('modal-open');
    };
    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
      setTimeout(() => { modal.hidden = true; }, 260);
    };
    document.addEventListener('click', e => {
      if (e.target.closest('[data-size-guide-open]')) { e.preventDefault(); open(); }
      else if (e.target.closest('[data-size-guide-close]')) close();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  })();

  // ---------- PDP image zoom lightbox ----------
  // Click any `.pd-gallery__image` → fullscreen viewer with the current slide
  // at the highest CDN size. Left/right arrows (mouse + keyboard) cycle
  // through all product media. Mobile users can pinch-zoom natively.
  (function initImageZoom() {
    const gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;
    const slides = Array.from(gallery.querySelectorAll('.pd-gallery__slide img'));
    if (!slides.length) return;

    // Build lightbox once, injected at body-end
    const lightbox = document.createElement('div');
    lightbox.className = 'pd-zoom-lightbox';
    lightbox.hidden = true;
    lightbox.setAttribute('aria-hidden', 'true');
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.innerHTML = ''
      + '<div class="pd-zoom-lightbox__backdrop" data-zoom-close></div>'
      + '<button type="button" class="pd-zoom-lightbox__close" data-zoom-close aria-label="Close">'
        + '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
      + '</button>'
      + '<button type="button" class="pd-zoom-lightbox__nav pd-zoom-lightbox__nav--prev" data-zoom-prev aria-label="Previous image">'
        + '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>'
      + '</button>'
      + '<button type="button" class="pd-zoom-lightbox__nav pd-zoom-lightbox__nav--next" data-zoom-next aria-label="Next image">'
        + '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>'
      + '</button>'
      + '<figure class="pd-zoom-lightbox__figure"><img class="pd-zoom-lightbox__image" alt=""></figure>'
      + '<span class="pd-zoom-lightbox__count" data-zoom-count></span>';
    document.addEventListener('DOMContentLoaded', () => document.body.appendChild(lightbox), { once: true });
    if (document.readyState !== 'loading') document.body.appendChild(lightbox);

    const img = lightbox.querySelector('.pd-zoom-lightbox__image');
    const countEl = lightbox.querySelector('[data-zoom-count]');
    let idx = 0;

    const srcFor = (i) => {
      const el = slides[i];
      if (!el) return '';
      // Bump the requested width to 2000px for the lightbox
      return (el.currentSrc || el.src || '').replace(/_(?:600|900|1200|1400|1600)x/, '_2000x');
    };

    const paint = () => {
      if (!slides[idx]) return;
      img.src = srcFor(idx);
      img.alt = slides[idx].alt || '';
      if (countEl) countEl.textContent = (idx + 1) + ' / ' + slides.length;
      // Hide arrows when only 1 image
      lightbox.querySelectorAll('.pd-zoom-lightbox__nav').forEach(b => {
        b.hidden = slides.length < 2;
      });
    };
    const open = (startIdx) => {
      idx = startIdx || 0;
      paint();
      lightbox.hidden = false;
      requestAnimationFrame(() => lightbox.classList.add('is-open'));
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('modal-open');
    };
    const close = () => {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
      setTimeout(() => { lightbox.hidden = true; }, 220);
    };
    const next = () => { idx = (idx + 1) % slides.length; paint(); };
    const prev = () => { idx = (idx - 1 + slides.length) % slides.length; paint(); };

    // Click main image opens lightbox at the active slide
    gallery.querySelector('[data-gallery-main]')?.addEventListener('click', e => {
      const clicked = e.target.closest('.pd-gallery__slide img');
      if (!clicked) return;
      const activeIdx = slides.findIndex(s => s === clicked);
      open(activeIdx >= 0 ? activeIdx : 0);
    });

    lightbox.addEventListener('click', e => {
      if (e.target.closest('[data-zoom-close]')) close();
      else if (e.target.closest('[data-zoom-next]')) next();
      else if (e.target.closest('[data-zoom-prev]')) prev();
    });
    document.addEventListener('keydown', e => {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    });

    // Add a "zoom" cursor hint on the main image so users know it's clickable
    gallery.querySelectorAll('.pd-gallery__slide img').forEach(el => {
      el.style.cursor = 'zoom-in';
    });
  })();

  // ---------- Sticky mobile ATC bar (PDP only) ----------
  // Playbook (AllYouNeedFresh: +51% mobile transactions): enlarge and increase
  // contrast of the conversion CTA and keep it in the viewport on mobile.
  // Observes the main ATC button directly (not a sentinel) — IntersectionObserver
  // fires reliably on state flip (visible → not visible) even during fast scrolls,
  // whereas a sentinel that starts off-screen never flips state and misses.
  // Click on the sticky button proxies to the main ATC — so quantity, variant,
  // personalization are always in sync with the primary form.
  (function initStickyATC() {
    const bar       = document.querySelector('[data-pd-sticky-atc]');
    const submit    = document.querySelector('[data-pd-sticky-submit]');
    const label     = document.querySelector('[data-pd-sticky-label]');
    const priceWrap = document.querySelector('[data-pd-sticky-price]');
    const mainBtn   = document.querySelector('[data-product-submit]');
    const mainText  = document.querySelector('[data-product-submit-text]');
    const productPrice = document.querySelector('[data-product-price]');
    if (!bar || !submit || !mainBtn) return;

    const show = () => {
      bar.hidden = false;
      requestAnimationFrame(() => bar.classList.add('is-visible'));
      bar.setAttribute('aria-hidden', 'false');
    };
    const hide = () => {
      bar.classList.remove('is-visible');
      bar.setAttribute('aria-hidden', 'true');
      setTimeout(() => { bar.hidden = true; }, 200);
    };

    const isMobile = () => !window.matchMedia('(min-width: 834px)').matches;

    // Watch the main ATC button — starts intersecting (visible), flips to not
    // intersecting when user scrolls it out of view. rootMargin adds a 60px
    // buffer so the sticky appears just before the main ATC fully leaves.
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!isMobile()) { hide(); return; }
        if (entry.isIntersecting) hide();
        else show();
      });
    }, { threshold: 0, rootMargin: '-60px 0px 0px 0px' });
    io.observe(mainBtn);

    // Also hide immediately when viewport crosses the mobile breakpoint
    window.matchMedia('(min-width: 834px)').addEventListener('change', e => {
      if (e.matches) hide();
    });

    // Sync sticky label + disabled state with the main button on variant change
    if (mainText) {
      const sync = () => {
        submit.disabled = mainBtn.disabled;
        if (label) label.textContent = mainText.textContent.trim();
        // Mirror the PDP price block (includes strike-through when on sale)
        if (priceWrap && productPrice) priceWrap.innerHTML = productPrice.innerHTML;
      };
      new MutationObserver(sync).observe(mainText, { childList: true, characterData: true, subtree: true });
      if (productPrice) new MutationObserver(sync).observe(productPrice, { childList: true, subtree: true });
      new MutationObserver(sync).observe(mainBtn, { attributes: true, attributeFilter: ['disabled'] });
    }

    // Delegate the sticky click to the main ATC button — the existing global
    // form-submit interceptor handles AJAX + drawer open.
    submit.addEventListener('click', () => {
      if (mainBtn.disabled) return;
      mainBtn.click();
    });
  })();

  // ---------- PDP social share: copy-link button ----------
  (function initShareCopy() {
    const btn = document.querySelector('[data-share-copy]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const url = window.location.href;
      const originalHTML = btn.innerHTML;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          // Legacy fallback
          const ta = document.createElement('textarea');
          ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        btn.classList.add('is-copied');
        // Swap icon to checkmark briefly
        btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
        window.BYNToast?.show('Link copied to clipboard');
        setTimeout(() => {
          btn.classList.remove('is-copied');
          btn.innerHTML = originalHTML;
        }, 1600);
      } catch (err) {
        console.error('[share copy]', err);
      }
    });
  })();

  // ---------- Facet country rail expand toggle (mobile) ----------
  // On mobile the "Shop by country" chips wrap into rows clamped at ~3 rows
  // with a fade. Tap the toggle button to reveal the rest of the ~48 flags.
  (function initFacetCountryToggle() {
    const rail = document.querySelector('[data-facet-country]');
    if (!rail) return;
    const btn = rail.querySelector('[data-facet-country-toggle]');
    const label = rail.querySelector('[data-facet-country-toggle-label]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const expanded = rail.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (label) label.textContent = expanded ? 'Show fewer countries' : 'Show all countries';
    });
  })();

  // ---------- Quick-add on product cards ----------
  // Delegated click handler: any [data-quick-add-variant="ID"] button POSTs to
  // /cart/add.js with that variant id. Success → open cart drawer + toast.
  // Sold-out sizes are disabled at render time so they never reach this handler.
  (function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add-variant]');
      if (!btn) return;
      // Never navigate — button is outside the anchor but stop bubbling anyway
      e.preventDefault();
      e.stopPropagation();
      if (btn.disabled) return;

      const original = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span>Adding…</span>';
      try {
        const fd = new FormData();
        fd.append('id', btn.dataset.quickAddVariant);
        fd.append('quantity', '1');
        const res = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('Add failed');
        // Fire the same event the wishlist path uses so cart badge/drawer refresh
        window.dispatchEvent(new CustomEvent('byn:cart-changed'));
        window.BYNToast?.show('Added to cart');
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg><span>Added</span>';
        setTimeout(() => {
          btn.disabled = false;
          btn.innerHTML = original;
        }, 1500);
      } catch (err) {
        console.error('[quick-add]', err);
        window.BYNToast?.show('Could not add — try again', { type: 'error' });
        btn.disabled = false;
        btn.innerHTML = original;
      }
    });
  })();

  // ---------- Recently viewed products ----------
  // Tracks product handles in localStorage on PDP visit; renders as a strip on
  // any page that includes [data-recently-viewed]. Fetches each product's
  // details via /products/<handle>.js so we get live pricing + featured image
  // without stale server-rendered snapshots.
  (function initRecentlyViewed() {
    const KEY = 'byn.recentlyViewed.v1';
    const CAP = 16;

    const read = () => {
      try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.filter(h => typeof h === 'string' && h) : [];
      } catch { return []; }
    };
    const write = (arr) => {
      try { localStorage.setItem(KEY, JSON.stringify(arr.slice(0, CAP))); }
      catch { /* silently skip */ }
    };
    const push = (handle) => {
      if (!handle) return;
      const cur = read().filter(h => h !== handle);
      cur.unshift(handle);
      write(cur);
    };

    // 1) Track the current product if on a PDP
    const productMeta = document.querySelector('[data-product-meta]');
    if (productMeta) {
      // Read handle from URL (/products/<handle>[?...])
      const m = location.pathname.match(/\/products\/([^\/\?]+)/);
      if (m) push(m[1]);
    }

    // 2) Render into any [data-recently-viewed] section
    const section = document.querySelector('[data-recently-viewed]');
    if (!section) return;

    const limit = Math.max(4, parseInt(section.dataset.limit || '8', 10));
    const excludeCurrent = section.dataset.excludeCurrent || '';
    const grid = section.querySelector('[data-recently-viewed-grid]');
    const clearBtn = section.querySelector('[data-recently-viewed-clear]');
    if (!grid) return;

    const escapeHtml = (str) => String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const formatMoney = (cents) => '$' + (cents / 100).toFixed(2);

    async function fetchProduct(handle) {
      try {
        const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } });
        if (!res.ok) return null;
        return await res.json();
      } catch { return null; }
    }

    async function renderStrip() {
      const handles = read()
        .filter(h => h !== excludeCurrent)
        .slice(0, limit);
      if (!handles.length) { section.hidden = true; return; }

      const products = await Promise.all(handles.map(fetchProduct));
      const valid = products.filter(p => p && p.id);
      if (!valid.length) { section.hidden = true; return; }

      grid.innerHTML = valid.map(p => {
        const img = (p.featured_image || '').replace(/(\.(?:jpe?g|png|webp|gif|avif))(\?.*)?$/i, '_600x$1$2');
        const onSale = p.compare_at_price && p.compare_at_price > p.price;
        const discount = onSale ? Math.round((p.compare_at_price - p.price) * 100 / p.compare_at_price) : 0;

        // Mirror the Liquid product-card 3-tier quick-add pattern:
        //   • single variant       → direct "Add to cart" pill
        //   • has "Size" option ONLY → chip strip with each size
        //   • multi-option         → "View options" link to PDP
        const options = p.options || [];
        const variants = p.variants || [];
        const sizeIdx = options.findIndex(o => (o.name || o).toString().toLowerCase() === 'size');
        const hasSize = sizeIdx >= 0;
        const onlySize = hasSize && options.length === 1;
        const isDefaultOnly = variants.length === 1 && (variants[0].title === 'Default Title' || options.length === 0);

        let quickHtml = '';
        if (isDefaultOnly) {
          const v = variants[0];
          if (v && v.available) {
            quickHtml = ''
              + '<div class="product-card__quick" data-product-quick>'
                + '<button type="button" class="product-card__quick-btn" data-quick-add-variant="' + v.id + '">'
                  + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
                    + '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>'
                    + '<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>'
                  + '</svg>'
                  + '<span>Add to cart</span>'
                + '</button>'
              + '</div>';
          } else {
            quickHtml = '<div class="product-card__quick" data-product-quick><button type="button" class="product-card__quick-btn" disabled>Sold out</button></div>';
          }
        } else if (onlySize) {
          const sizes = variants.map(v => {
            const label = escapeHtml(v['option' + (sizeIdx + 1)] || v.title || '?');
            const cls = 'product-card__quick-size' + (v.available ? '' : ' is-unavailable');
            const attrs = v.available
              ? 'data-quick-add-variant="' + v.id + '"'
              : 'disabled aria-label="' + label + ' — sold out"';
            return '<button type="button" class="' + cls + '" ' + attrs + ' title="' + escapeHtml(v.title || '') + '">' + label + '</button>';
          }).join('');
          quickHtml = ''
            + '<div class="product-card__quick product-card__quick--sizes" data-product-quick>'
              + '<span class="product-card__quick-label">Quick add</span>'
              + '<div class="product-card__quick-sizes">' + sizes + '</div>'
            + '</div>';
        } else {
          quickHtml = ''
            + '<a href="' + escapeHtml(p.url) + '" class="product-card__quick product-card__quick--link" data-product-quick>'
              + '<span class="product-card__quick-btn product-card__quick-btn--link">'
                + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
                  + '<polyline points="9 18 15 12 9 6"/>'
                + '</svg>'
                + '<span>View options</span>'
              + '</span>'
            + '</a>';
        }

        return ''
          + '<article class="product-card" data-product-handle="' + escapeHtml(p.handle) + '">'
            + '<button type="button" class="product-card__wishlist" data-wishlist-toggle'
              + ' data-wishlist-handle="' + escapeHtml(p.handle) + '"'
              + ' data-wishlist-title="' + escapeHtml(p.title) + '"'
              + ' data-wishlist-url="' + escapeHtml(p.url) + '"'
              + ' data-wishlist-image="' + escapeHtml(img) + '"'
              + ' data-wishlist-price="' + escapeHtml(formatMoney(p.price)) + '"'
              + ' aria-label="Add to wishlist" aria-pressed="false">'
              + '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
                + '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>'
              + '</svg>'
            + '</button>'
            + '<a href="' + escapeHtml(p.url) + '" class="product-card__link">'
              + '<div class="product-card__media">'
                + (onSale ? '<span class="product-card__badge product-card__badge--sale"><span class="product-card__badge-pct">-' + discount + '%</span></span>' : '')
                + (img ? '<img class="product-card__image product-card__image--primary" src="' + escapeHtml(img) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '')
              + '</div>'
              + '<div class="product-card__info">'
                + '<h3 class="product-card__title">' + escapeHtml(p.title) + '</h3>'
                + '<div class="product-card__price">'
                  + (onSale
                    ? '<span class="product-card__price--sale">' + formatMoney(p.price) + '</span>'
                      + '<s class="product-card__price--compare">' + formatMoney(p.compare_at_price) + '</s>'
                    : '<span class="product-card__price--regular">' + formatMoney(p.price) + '</span>')
                + '</div>'
              + '</div>'
            + '</a>'
            + quickHtml
          + '</article>';
      }).join('');
      // Re-sync wishlist button states + quick-add click delegation (already
      // global via document.addEventListener 'click' in initQuickAdd) work
      // automatically for the freshly-injected cards.
      section.hidden = false;
    }

    renderStrip();

    clearBtn?.addEventListener('click', () => {
      write([]);
      grid.innerHTML = '';
      section.hidden = true;
    });
  })();

  // ---------- Announcement bar rotator ----------
  // Cross-fades between multiple `[data-announcement-message]` children on the
  // interval set by `data-interval` (seconds). Skips gracefully with 0 or 1 msg.
  (function initAnnouncementBar() {
    const bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    const msgs = Array.from(bar.querySelectorAll('[data-announcement-message]'));
    if (msgs.length < 2) return;
    const interval = Math.max(2, parseInt(bar.dataset.interval || '4', 10)) * 1000;
    let i = 0;
    setInterval(() => {
      msgs[i].classList.remove('is-active');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-active');
    }, interval);
  })();

  // ---------- Wishlist (localStorage-backed save-for-later) ----------
  // Playbook: secondary CTA capturing lower-commitment intent. Purely client-side
  // (localStorage byn.wishlist.v1); no server round-trip. All product-card hearts,
  // PDP save buttons, header icon + count, and the wishlist drawer share this state.
  (function initWishlist() {
    const KEY = 'byn.wishlist.v1';

    const read = () => {
      try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.filter(o => o && o.handle) : [];
      } catch { return []; }
    };
    const write = (arr) => {
      try { localStorage.setItem(KEY, JSON.stringify(arr)); }
      catch { /* storage full or blocked */ }
    };

    const escapeHtml = (str) => String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const has = (handle) => read().some(o => o.handle === handle);
    const add = (item) => {
      const arr = read().filter(o => o.handle !== item.handle);
      arr.unshift(item);
      write(arr);
      sync();
    };
    const remove = (handle) => {
      write(read().filter(o => o.handle !== handle));
      sync();
    };

    const syncButtons = () => {
      document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
        const handle = btn.dataset.wishlistHandle;
        const active = has(handle);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        const label = btn.querySelector('[data-wishlist-btn-label]');
        if (label) label.textContent = active ? 'Saved' : 'Save to wishlist';
      });
    };

    const syncCount = () => {
      const n = read().length;
      document.querySelectorAll('[data-wishlist-count]').forEach(el => {
        el.textContent = n > 0 ? n : '';
      });
      const drawerCount = document.querySelector('[data-wishlist-drawer-count]');
      if (drawerCount) drawerCount.textContent = n;
    };

    // Parse a formatted price like "$29.99" back to cents for totalling
    const parsePrice = (s) => {
      if (!s) return 0;
      const m = String(s).match(/([\d,]+(?:\.\d+)?)/);
      if (!m) return 0;
      return Math.round(parseFloat(m[1].replace(/,/g, '')) * 100);
    };
    const formatMoney = (cents) => '$' + (cents / 100).toFixed(2);

    const renderDrawer = () => {
      const itemsEl = document.querySelector('[data-wishlist-items]');
      const emptyEl = document.querySelector('[data-wishlist-empty]');
      const footerEl = document.querySelector('[data-wishlist-footer]');
      const totalEl = document.querySelector('[data-wishlist-total]');
      if (!itemsEl || !emptyEl) return;
      const items = read();
      if (!items.length) {
        itemsEl.innerHTML = '';
        emptyEl.hidden = false;
        if (footerEl) footerEl.hidden = true;
        return;
      }
      emptyEl.hidden = true;
      if (footerEl) footerEl.hidden = false;

      let totalCents = 0;
      itemsEl.innerHTML = items.map(item => {
        const priceCents = parsePrice(item.price);
        totalCents += priceCents;
        return ''
          + '<div class="wishlist-drawer__item" data-wishlist-item-handle="' + escapeHtml(item.handle) + '">'
            + '<a href="' + escapeHtml(item.url || '#') + '" class="wishlist-drawer__item-image">'
              + (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title || '') + '" loading="lazy">' : '')
            + '</a>'
            + '<div class="wishlist-drawer__item-body">'
              + '<p class="wishlist-drawer__item-title"><a href="' + escapeHtml(item.url || '#') + '">' + escapeHtml(item.title || '') + '</a></p>'
              + (item.price ? '<p class="wishlist-drawer__item-price">' + escapeHtml(item.price) + '</p>' : '')
              + '<div class="wishlist-drawer__item-actions">'
                + '<button type="button" class="wishlist-drawer__add-btn" data-wishlist-add="' + escapeHtml(item.handle) + '">'
                  + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
                    + '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>'
                    + '<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>'
                  + '</svg>'
                  + '<span>Add to cart</span>'
                + '</button>'
                + '<button type="button" class="wishlist-drawer__remove" data-wishlist-remove="' + escapeHtml(item.handle) + '" aria-label="Remove ' + escapeHtml(item.title || '') + '">'
                  + '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
                    + '<polyline points="3 6 5 6 21 6"/><path d="M19 6l-1.34 14a2 2 0 0 1-2 1.79H8.34A2 2 0 0 1 6.34 20L5 6"/>'
                  + '</svg>'
                + '</button>'
              + '</div>'
            + '</div>'
          + '</div>';
      }).join('');

      if (totalEl) totalEl.textContent = totalCents > 0 ? formatMoney(totalCents) : '—';
    };

    // Add a single wishlist item to cart via its first available variant
    async function addWishlistItemToCart(handle, btn) {
      const original = btn?.innerHTML;
      try {
        if (btn) {
          btn.disabled = true;
          btn.innerHTML = '<span>Adding…</span>';
        }
        const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('Product not found');
        const product = await res.json();
        const variant = (product.variants || []).find(v => v.available) || product.variants?.[0];
        if (!variant) throw new Error('No variant available');
        const fd = new FormData();
        fd.append('id', String(variant.id));
        fd.append('quantity', '1');
        const addRes = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } });
        if (!addRes.ok) throw new Error('Cart add failed');
        // Refresh the cart drawer if available
        window.dispatchEvent(new CustomEvent('byn:cart-changed'));
        window.BYNToast?.show('Added ' + product.title.substring(0, 40) + (product.title.length > 40 ? '…' : '') + ' to cart');
        if (btn) {
          btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg><span>Added</span>';
          setTimeout(() => { btn.disabled = false; btn.innerHTML = original; }, 1600);
        }
        return true;
      } catch (err) {
        console.error('[wishlist add]', err);
        window.BYNToast?.show('Could not add — item may be sold out', { type: 'error' });
        if (btn) { btn.disabled = false; btn.innerHTML = original; }
        return false;
      }
    }

    // Move-all handler
    async function moveAllToCart(btn) {
      const items = read();
      if (!items.length) return;
      const original = btn?.innerHTML;
      if (btn) { btn.disabled = true; btn.innerHTML = '<span>Adding…</span>'; }
      let added = 0;
      for (const item of items) {
        const ok = await addWishlistItemToCart(item.handle, null);
        if (ok) added++;
      }
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = original;
      }
      window.BYNToast?.show('Added ' + added + ' item' + (added === 1 ? '' : 's') + ' to cart');
    }

    // Share wishlist link — copy a URL with handles as query param
    async function shareWishlist(btn) {
      const items = read();
      if (!items.length) return;
      const handles = items.map(i => i.handle).join(',');
      const url = window.location.origin + '/?wishlist=' + encodeURIComponent(handles);
      try {
        if (navigator.share) {
          await navigator.share({ title: 'My wishlist', url });
          return;
        }
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const ta = document.createElement('textarea');
          ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
          document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
        }
        window.BYNToast?.show('Wishlist link copied');
      } catch (err) {
        console.error('[wishlist share]', err);
      }
    }

    const sync = () => { syncButtons(); syncCount(); renderDrawer(); };

    // Toggle heart clicks — event delegation so it works for cards added later
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.dataset.wishlistHandle;
      if (!handle) return;
      if (has(handle)) {
        remove(handle);
        window.BYNToast?.show('Removed from wishlist', { type: 'info' });
      } else {
        add({
          handle,
          title: btn.dataset.wishlistTitle || '',
          url: btn.dataset.wishlistUrl || '',
          image: btn.dataset.wishlistImage || '',
          price: btn.dataset.wishlistPrice || ''
        });
        window.BYNToast?.show('Added to wishlist');
      }
    });

    // Drawer open/close
    const drawer = document.querySelector('[data-wishlist-drawer]');
    if (drawer) {
      const open = () => {
        drawer.hidden = false;
        document.body.classList.add('wishlist-open');
        requestAnimationFrame(() => drawer.classList.add('is-open'));
        drawer.setAttribute('aria-hidden', 'false');
        renderDrawer();
      };
      const close = () => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('wishlist-open');
        setTimeout(() => { drawer.hidden = true; }, 300);
      };

      document.addEventListener('click', e => {
        if (e.target.closest('[data-wishlist-drawer-open]')) { e.preventDefault(); open(); }
        else if (e.target.closest('[data-wishlist-drawer-close]')) close();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !drawer.hidden) close();
      });

      // Remove / Add-to-cart / Move-all / Share clicks inside the drawer
      drawer.addEventListener('click', e => {
        const rm = e.target.closest('[data-wishlist-remove]');
        if (rm) { remove(rm.dataset.wishlistRemove); return; }
        const addBtn = e.target.closest('[data-wishlist-add]');
        if (addBtn) { addWishlistItemToCart(addBtn.dataset.wishlistAdd, addBtn); return; }
        const moveAll = e.target.closest('[data-wishlist-move-all]');
        if (moveAll) { moveAllToCart(moveAll); return; }
        const share = e.target.closest('[data-wishlist-share]');
        if (share) { shareWishlist(share); return; }
      });
    }

    // Refresh cart drawer when wishlist adds an item (so the mini cart count
    // + subtotal updates without needing to close/reopen)
    window.addEventListener('byn:cart-changed', () => {
      // The CartDrawer IIFE has its own refresh; fire an event it listens to,
      // or reload the cart via /cart.js and update badges directly here as a fallback.
      const countEls = document.querySelectorAll('[data-cart-count]');
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(r => r.json())
        .then(cart => {
          countEls.forEach(el => { el.textContent = cart.item_count > 0 ? cart.item_count : ''; });
        }).catch(() => {});
    });

    // Initial paint
    sync();

    // Cross-tab sync — respond to localStorage changes in other tabs
    window.addEventListener('storage', e => {
      if (e.key === KEY) sync();
    });
  })();

  // ---------- Personalization inputs (Name / Number on product form) ----------
  (function () {
    const blocks = document.querySelectorAll('[data-personalize]');
    blocks.forEach(block => {
      const nameIn = block.querySelector('[data-personalize-name]');
      const nameCounter = block.querySelector('[data-personalize-counter]');
      const numIn = block.querySelector('[data-personalize-number]');
      const numCounter = block.querySelector('[data-personalize-number-counter]');

      if (nameIn && nameCounter) {
        const max = parseInt(nameIn.getAttribute('data-max'), 10) || 12;
        const update = () => {
          const len = nameIn.value.length;
          nameCounter.textContent = len + '/' + max;
          nameCounter.style.color = len >= max ? '#DD3C3C' : '';
        };
        nameIn.addEventListener('input', update);
        update();
      }

      if (numIn) {
        const max = parseInt(numIn.getAttribute('data-max'), 10) || 99;
        const digits = parseInt(numIn.getAttribute('data-digits'), 10) || String(max).length;
        const update = () => {
          if (numCounter) {
            const len = numIn.value.length;
            numCounter.textContent = len + '/' + digits;
            numCounter.style.color = len >= digits ? '#DD3C3C' : '';
          }
        };
        numIn.addEventListener('input', () => {
          // Strip non-digits, clamp to max
          let v = numIn.value.replace(/\D+/g, '');
          if (v && parseInt(v, 10) > max) v = String(max);
          if (v !== numIn.value) numIn.value = v;
          update();
        });
        update();
      }
    });
  })();

  // ---------- Add-to-cart form intercept ----------
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const button   = form.querySelector('[type="submit"]');
    const textNode = button?.querySelector('[data-product-submit-text]') || button;
    const originalText = textNode?.textContent;
    if (button) { button.disabled = true; }
    if (textNode) { textNode.textContent = 'Adding...'; }
    try {
      const formData = new FormData(form);
      if (CartDrawer) {
        await CartDrawer.addItem(formData);
      } else {
        const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('Add to cart failed');
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = cart.item_count > 0 ? cart.item_count : '');
      }
      if (textNode) textNode.textContent = '✓ Added';
      setTimeout(() => {
        if (textNode) textNode.textContent = originalText;
        if (button) button.disabled = false;
      }, 1500);
    } catch (err) {
      console.error(err);
      if (textNode) textNode.textContent = originalText;
      if (button) button.disabled = false;
      alert(err.message || 'Could not add to cart. Please try again.');
    }
  });

  // ---------- Localization auto-submit ----------
  document.querySelectorAll('.site-footer__locale select').forEach(sel => {
    sel.addEventListener('change', e => e.target.form.submit());
  });

})();
